
/*
 * ----------------------------------------------------------------------------------
 * Imports - External
 * ----------------------------------------------------------------------------------
 */
import { AlertTitle, Box, Button, Dialog, Divider, Grid2, styled, Table, TableCell, TableRow, Typography } from '@mui/material';
import {
    AuthenticatedUserContext,
    Checkbox,
    CollaboratingGroupContext,
    CrfCondition,
    CrfForm,
    FormBreadcrumbs,
    FormOptionsContext,
    FormSubmitButton,
    GetFieldLookupItem,
    GroupedField,
    GroupedFields,
    GroupedTable,
    GroupedTableArrayField,
    GroupedTableRow,
    ICrfConditionParameters,
    IGroupedTableArrayFieldColumn,
    IGroupedTableCellProps,
    IGroupedTableHeaderProps,
    Input,
    InputOptionMode,
    InstitutionContext,
    LookupsContext,
    MasterGroupContext,
    Numeric,
    OnlinePatientManagementContext,
    PageLayout,
    PatientContext,
    RouteLoading,
    Select,
    Text,
    useIsMobile,
    useSnackbar
} from '@ngt/opms';
import { Permission, usePermissionsByIds } from '@ngt/opms-bctapi';
import React, { FC, FunctionComponent, MouseEvent, PropsWithChildren, useCallback, useContext } from 'react';

/*
 * ----------------------------------------------------------------------------------
 * Imports - Internal
 * ----------------------------------------------------------------------------------
 */
import { ConditionMode, IFormActions, IFormSubmitFailed, useScopedField, ValidateOn } from '@ngt/forms-core';
import { useNavigate, useParams } from 'react-router-dom';
import * as Dtos from '../../../api/dtos';
import useGetImageReportUploadContent from '../../../hooks/data/useGetImageReportUploadContent';
import SourceDocuments from '../../documents/SourceDocuments';
import RadiologistSelector from '../fields/RadiologistSelector';
import InputDisplay from '../inputs/InputDisplay';
import ImageReportUploadView from '../views/ImageReportUploadView';
import { AppSettings } from '../../../AppSettings';


/*
 * ----------------------------------------------------------------------------------
 * Interface
 * ----------------------------------------------------------------------------------
 */

interface IRadiologyReportFormProps { }

interface IMammographyReportPrimaryLesionRowProps { }
interface IMammographyReportLesionGridProps { }

interface IUltrasoundReportPrimaryLesionRowProps { }
interface IUltrasoundReportLesionGridProps { }

interface IMRIReportPrimaryLesionRowProps { }
interface IMRIReportLesionGridProps { }

interface ICEMReportPrimaryLesionRowProps { }
interface ICEMReportLesionGridProps { }


interface ISummaryReportProps {
    dialogOpen: boolean,
    setDialogOpen: (open: boolean) => void
}

interface ISubmitButtonsProps {
    onSubmit?: (reportComplete?: boolean | null, formActions?: IFormActions) => void
}

/*
 * ---------------------------------------------------------------------------------
 * Styles
 * ---------------------------------------------------------------------------------
 */

const Subheading = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(3, 2, 3, 2)
}));

const StyledButtonGroup = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'row',
    padding: theme.spacing(3),
    gap: theme.spacing(3),
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'end',

    [theme.breakpoints.only('xs')]: {
        flexDirection: 'column',
        textAlign: 'right',
        gap: theme.spacing(2),

        '& > *': {
            width: '100%'
        }
    }
}))

const ReportTitle = styled(Typography)(() => ({
    paddingLeft: '1rem', 
    marginTop: '1.5rem', 
    marginBottom: '1.5rem', 
    fontSize: '1.5rem'

}));

const ReportHeader = styled(Typography)(() => ({
    paddingLeft: '1rem', 
    marginBottom: '-0.5rem', 
    fontSize: '1.25rem'

}));

const ReportTableContainer = styled(Box)(() => ({
        display: 'flex'

}));

const ReportTable = styled(Table)(() => ({
    border: '1px solid rgba(224, 224, 224, 1)',
    margin: '1rem', 

    '& tr': {
        border: 1
    }, 
    '& td': {
        padding: '8px'
    }

}));

const ViewReportButtonContainer = styled(Box)(() => ({
    marginLeft: '1rem', 
    marginRight: '1rem'
}));


/*
 * ---------------------------------------------------------------------------------
 * Functions
 * ---------------------------------------------------------------------------------
 */
const mriClassificationDiscontinuous = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.RadiologyReport>) => { 
    const regex = /mriLesions\[(?<Index>[0-9]*)].(?<GroupedField>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const classification = formState?.value?.mriLesions?.[+idx]?.secondaryLesionClassification;

    return classification === Dtos.LookupLesionClassificationType.DiscontiguousWithIndex;
};

const ultrasoundPrimaryLesionTypeSpecify = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.RadiologyReport>) => { 
    const regex = /ultrasoundLesions\[(?<Index>[0-9]*)].(?<GroupedField>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const primaryType = formState?.value?.ultrasoundLesions?.[+idx]?.primaryLesionType;

    return primaryType === Dtos.LookupUltrasoundLesionType.Other;
};
const ultrasoundSecondaryLesionTypeSpecify = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.RadiologyReport>) => {
    const regex = /ultrasoundLesions\[(?<Index>[0-9]*)].(?<GroupedField>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const secondaryType = formState?.value?.ultrasoundLesions?.[+idx]?.secondaryLesionType;

    return secondaryType === Dtos.LookupUltrasoundLesionType.Other;
}

const cemLesionCorrespondance = ({ formState, parentFieldName }: ICrfConditionParameters<Dtos.RadiologyReport>) => {
    const regex = /cemLesions\[(?<Index>[0-9]*)].(?<GroupedField>.*)/;
    const res = regex.exec(parentFieldName ?? "");
    const idx = res?.groups?.['Index'];

    if (!idx) return false;

    const correspondance = formState?.value?.cemLesions?.[+idx]?.lesionCorrespondance;

    return correspondance === Dtos.LookupYesNoType.Yes;
}

const mammographyPerformed = ({ formState }: ICrfConditionParameters<Dtos.RadiologyReport>) => !formState?.value?.mammographyPerformed;
const ultrasoundPerformed = ({ formState }: ICrfConditionParameters<Dtos.RadiologyReport>) => !formState?.value?.ultrasoundPerformed;
const cemPerformed = ({ formState }: ICrfConditionParameters<Dtos.RadiologyReport>) => !formState?.value?.cemPerformed;

const reportComplete = ({ formState }: ICrfConditionParameters<Dtos.RadiologyReport>) => formState?.value?.completedBy != null;
const reportNotComplete = ({ formState }: ICrfConditionParameters<Dtos.RadiologyReport>) => formState?.value?.completedBy == null;


/*
 * ----------------------------------------------------------------------------------
 * Components
 * ----------------------------------------------------------------------------------
 */


enum ReadOnlyDisplayMode {
    ShowWhenReadOnly,
    HideWhenReadOnly
}

interface IReadOnlyDisplayProps extends PropsWithChildren {
    readOnly?: boolean;
    mode: ReadOnlyDisplayMode;
}

const ReadOnlyDisplay: FunctionComponent<IReadOnlyDisplayProps> = ({
    readOnly,
    mode,
    children
}) => {
    const { readOnly: formReadOnly } = useContext(FormOptionsContext);

    const isReadOnly = readOnly || formReadOnly;
    if (isReadOnly && mode === ReadOnlyDisplayMode.HideWhenReadOnly || !isReadOnly && mode === ReadOnlyDisplayMode.ShowWhenReadOnly) {
        return null;
    }

    return children;
}

const mammographyNotDoneKey = "mammographyPerformed";
const mammographyLesionsKey = "mammographyLesions";
const ultrasoundNotDoneKey = "ultrasoundPerformed";
const ultrasoundLesionsKey = "ultrasoundLesions";
const mriLesionsKey = "mriLesions";
const cemLesionsKey = "cemLesions";
const parenchymalEnhancementScoreLeftKey = "parenchymalEnhancementScoreLeft";
const parenchymalEnhancementScoreRightKey = "parenchymalEnhancementScoreRight";
const cemParenchymalEnhancementScoreLeftKey = "cemParenchymalEnhancementScoreLeft";
const cemParenchymalEnhancementScoreRightKey = "cemParenchymalEnhancementScoreRight";
const cemNotDoneKey = "cemPerformed";

const MammographyLesionColumns: Array<IGroupedTableArrayFieldColumn<Dtos.MammographyReportLesion>> = [
    {
        propertyPath: 'lesionNumber',
        TableCellProps: {
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <>
                <Typography>
                    M<InputDisplay />
                </Typography>
            </>
        )
    },
    {
        propertyPath: 'secondaryLesionType',
        TableCellProps: {
            sx: {
                minWidth: 250,
                maxWidth: 250,
                width: 250
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionSide',
        TableCellProps: {
            sx: {
                minWidth: 130,
                maxWidth: 130,
                width: 130
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionLocation',
        TableCellProps: {
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionSize',
        TableCellProps: {
            sx: {
                minWidth: 130,
                maxWidth: 130,
                width: 130
            }
        },
        content: (
            <Input
                component={Numeric}
            />
        )
    },
    {
        propertyPath: 'secondaryLesionBirads',
        TableCellProps: {
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    }
];

const MammographyLesionHeader: IGroupedTableHeaderProps[] = [
    {
        columnName: 'lesionNumber',
        content: 'Lesion Number',
        TableCellProps: {
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        }
    },
    {
        columnName: 'lesionType',
        content: 'Lesion Type',
        TableCellProps: {
            sx: {
                minWidth: 250,
                maxWidth: 250,
                width: 250
            }
        }
    },
    {
        columnName: 'lesionSide',
        content: 'Side',
        TableCellProps: {
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        }
    },
    {
        columnName: 'lesionLocation',
        content: "Location (o'clock)",
        TableCellProps: {
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        }
    },
    {
        columnName: 'lesionSize',
        content: 'Size (mm)',
        TableCellProps: {
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        }
    },
    {
        columnName: 'primaryLesionBirads',
        content: 'BIRADS (Rolled up Mg/US score)',
        TableCellProps: {
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        }
    }
];

const LesionNumberUpdater: React.FunctionComponent<{lesionNumberPath: string, lesionNumber: number, notDonePath: string}> = ({
    lesionNumberPath,
    lesionNumber,
    notDonePath
}) => {
    const {
        context: {
            setValue: setLesionNumber
        }
    } = useScopedField<number>(lesionNumberPath, { value: true }, false, false);

    const {
        state: {
            value: notDone
        }
    } = useScopedField<number>(notDonePath, { value: true }, false, false);

    React.useEffect(() => {
        if (lesionNumber && !notDone)
        {
            setLesionNumber(lesionNumber);
        }
    }, [lesionNumber, setLesionNumber, notDone]);

    return (
        null
    );
}

const MammographyLesionCells: IGroupedTableCellProps[] = [
    {
        columnName: 'lesionNumber',
        content: (
            <>
                <Typography>M1</Typography>
                <LesionNumberUpdater 
                    lesionNumberPath={`${mammographyLesionsKey}[0].lesionNumber`}
                    lesionNumber={1}
                    notDonePath={mammographyNotDoneKey}
                />
            </>
        )
    },
    {
        columnName: 'lesionType',
        content: (
            <Input
                component={Select}
                name={`${mammographyLesionsKey}[0].primaryLesionType`}
            />
        ),
        propertyPath: `${mammographyLesionsKey}[0].primaryLesionType`
    },
    {
        columnName: 'lesionSide',
        content: (
            <Input
                component={Select}
                name={`${mammographyLesionsKey}[0].lesionSide`}
            />
        ),
        propertyPath: `${mammographyLesionsKey}[0].lesionSide`
    },
    {
        columnName: 'lesionLocation',
        content: (
            <Input
                component={Select}
                name={`${mammographyLesionsKey}[0].lesionLocation`}
            />
        ),
        propertyPath: `${mammographyLesionsKey}[0].lesionLocation`
    },
    {
        columnName: 'lesionSize',
        content: (
            <Input
                component={Numeric}
                name={`${mammographyLesionsKey}[0].lesionSize`}
            />
        ),
        propertyPath: `${mammographyLesionsKey}[0].lesionSize`
    },
    {
        columnName: 'primaryLesionBirads',
        content: (
            <Input
                component={Select}
                name={`${mammographyLesionsKey}[0].primaryLesionBirads`}
            />
        ),
        propertyPath: `${mammographyLesionsKey}[0].primaryLesionBirads`
    }
];

const MammographyReportPrimaryLesionRow: FC<IMammographyReportPrimaryLesionRowProps> = () => {
    return (
        <GroupedTable
            headers={MammographyLesionHeader}
        >
            <GroupedTableRow 
                cells={MammographyLesionCells}
            />
        </GroupedTable>
    );
}

const MammographyReportLesionFilter = (_item: Dtos.MammographyReportLesion | null | undefined, index: number, _items: Dtos.MammographyReportLesion[] | null | undefined) => {
    return index !== 0;
}

const MammographyReportLesionGrid: FC<IMammographyReportLesionGridProps> = () => {
    const createNewItem = useCallback((values: Dtos.MammographyReportLesion[] | null | undefined) => {
        if (!values) values = [];

        let newLesion = new Dtos.MammographyReportLesion ({
            lesionNumber: (values?.length ?? 0) + 1,
            enteredBy: "",
            modifiedBy: ""
        });

        return newLesion;
    }, []);
    
    return (
        <>
            <GroupedTableArrayField
                propertyPath={mammographyLesionsKey}
                columns={MammographyLesionColumns}
                createNewItem={createNewItem}
                filter={MammographyReportLesionFilter}
                entityName='Secondary Lesion'
            />
        </>
    );
}

const UltrasoundLesionColumns: Array<IGroupedTableArrayFieldColumn<Dtos.UltrasoundReportLesion>> = [
    {
        propertyPath: 'lesionNumber',
        TableCellProps:{
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <>
                <Typography>
                    U<InputDisplay />
                </Typography>
            </>
        )
    },
    {
        propertyPath: 'secondaryLesionType',
        TableCellProps:{
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionTypeSpecify',
        TableCellProps:{
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={ultrasoundSecondaryLesionTypeSpecify}
                subscription={{ value: true }}
            >
                <Input
                    component={Text}
                    name="lesionTypeSpecify"
                />
            </CrfCondition>
        )
    },
    {
        propertyPath: 'lesionSide',
        TableCellProps:{
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionLocation',
        TableCellProps:{
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionSize',
        TableCellProps:{
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <Input
                component={Numeric}
            />
        )
    }
];

const UltrasoundLesionHeader: IGroupedTableHeaderProps[] = [
    {
        columnName: 'lesionNumber',
        content: 'Lesion Number',
        TableCellProps:{
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
    },
    {
        columnName: 'lesionType',
        content: 'Lesion Type',
        TableCellProps:{
            sx: {
                width: '10%',
                minWidth: 200
            }
        }
    },
    {
        columnName: 'lesionTypeSpecify',
        content: 'Specify',
        TableCellProps:{
            sx: {
                width: '10%',
                minWidth: 200
            }
        }
    },
    {
        columnName: 'lesionSide',
        content: 'Side',
        TableCellProps:{
            sx: {
                width: '10%',
                minWidth: 200
            }
        }
    },
    {
        columnName: 'lesionLocation',
        content: "Location (o'clock)",
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 200
            }
        }
    },
    {
        columnName: 'lesionSize',
        content: 'Size (mm)',
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 200
            }
        }
    }
];

const UltrasoundLesionCells: IGroupedTableCellProps[] = [
    {
        columnName: 'lesionNumber',
        content: (
            <>
                <Typography>U1</Typography>
                <LesionNumberUpdater 
                    lesionNumberPath={`${ultrasoundLesionsKey}[0].lesionNumber`}
                    lesionNumber={1}
                    notDonePath={ultrasoundNotDoneKey}
                />
            </>
        )
    },
    {
        columnName: 'lesionType',
        content: (
            <Input
                component={Select}
                name={`${ultrasoundLesionsKey}[0].primaryLesionType`}
            />
        ),
        propertyPath: `${ultrasoundLesionsKey}[0].primaryLesionType`
    },
    {
        columnName: 'lesionTypeSpecify',
        content: (
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={ultrasoundPrimaryLesionTypeSpecify}
                subscription={{ value: true }}
            >
                <Input
                    component={Text}
                    name={`${ultrasoundLesionsKey}[0].lesionTypeSpecify`}
                />
            </CrfCondition>
        ),
        propertyPath: `${ultrasoundLesionsKey}[0].lesionTypeSpecify`
    },
    {
        columnName: 'lesionSide',
        content: (
            <Input
                component={Select}
                name={`${ultrasoundLesionsKey}[0].lesionSide`}
            />
        ),
        propertyPath: `${ultrasoundLesionsKey}[0].lesionSide`
    },
    {
        columnName: 'lesionLocation',
        content: (
            <Input
                component={Select}
                name={`${ultrasoundLesionsKey}[0].lesionLocation`}
            />
        ),
        propertyPath: `${ultrasoundLesionsKey}[0].lesionLocation`
    },
    {
        columnName: 'lesionSize',
        content: (
            <Input
                component={Numeric}
                name={`${ultrasoundLesionsKey}[0].lesionSize`}
            />
        ),
        propertyPath: `${ultrasoundLesionsKey}[0].lesionSize`
    }
];

const UltrasoundReportPrimaryLesionRow: FC<IUltrasoundReportPrimaryLesionRowProps> = () => {
    return (
        <GroupedTable
            headers={UltrasoundLesionHeader}
        >
            <GroupedTableRow 
                cells={UltrasoundLesionCells}
            />
        </GroupedTable>
    );
}

const UltrasoundReportLesionFilter = (_item: Dtos.UltrasoundReportLesion | null | undefined, index: number, _items: Dtos.MammographyReportLesion[] | null | undefined) => {
    return index !== 0;
}

const UltrasoundReportLesionGrid: FC<IUltrasoundReportLesionGridProps> = () => {
    const createNewItem = useCallback((values: Dtos.UltrasoundReportLesion[] | null | undefined) => {
        if (!values) values = [];

        let newLesion = new Dtos.UltrasoundReportLesion ({
            lesionNumber: (values?.length ?? 0) + 1,
            enteredBy: "",
            modifiedBy: ""
        });

        return newLesion;
    }, []);

    return (
        <>
            <GroupedTableArrayField
                propertyPath={ultrasoundLesionsKey}
                columns={UltrasoundLesionColumns}
                createNewItem={createNewItem}
                filter={UltrasoundReportLesionFilter}
                entityName='Secondary Lesion'
            />
        </>
    );
} 

const MRILesionColumns: Array<IGroupedTableArrayFieldColumn<Dtos.MRIReportLesion>> = [
    {
        propertyPath: 'lesionNumber',
        TableCellProps:{
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <>
                <Typography>
                    R<InputDisplay />
                </Typography>
            </>
        )
    },
    {
        propertyPath: 'lesionType',
        TableCellProps:{
            sx: {
                minWidth: 180,
                maxWidth: 180,
                width: 180
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionSide',
        TableCellProps:{
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionLocation',
        TableCellProps:{
            sx: {
                minWidth: 220,
                maxWidth: 220,
                width: 220
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionSize',
        TableCellProps:{
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <Input
                component={Numeric}
            />
        )
    },
    {
        propertyPath: 'secondaryLesionBirads',
        TableCellProps:{
            sx: {
                minWidth: 120,
                maxWidth: 120,
                width: 120
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'secondaryLesionClassification',
        TableCellProps:{
            sx: {
                minWidth: 220,
                maxWidth: 220,
                width: 220
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'secondaryLesionClassificationMinimumDistance',
        TableCellProps:{
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={mriClassificationDiscontinuous}
                subscription={{ value: true }}
            >
                <Input
                    component={Numeric}
                />
            </CrfCondition>
        )
    }
];

const MRILesionHeader: IGroupedTableHeaderProps[] = [
    {
        columnName: 'lesionNumber',
        content: 'Lesion Number',
        TableCellProps:{
            sx: {
                width: 150,
                minWidth: 150
            }
        }
    },
    {
        columnName: 'lesionType',
        content: 'Lesion Type',
        TableCellProps:{
            sx: {
                width: '10%',
                minWidth: 180
            }
        }
    },
    {
        columnName: 'lesionSide',
        content: 'Side',
        TableCellProps:{
            sx: {
                width: '10%',
                minWidth: 150
            }
        }
    },
    {
        columnName: 'lesionLocation',
        content: "Location (o'clock)",
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 220
            }
        }
    },
    {
        columnName: 'lesionSize',
        content: 'Size (mm)',
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 150
            }
        }
    },
    {
        columnName: 'lesionBirads',
        content: 'BIRADS',
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 120
            }
        }
    },
    {
        columnName: 'lesionClassification',
        content: 'Classification',
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 220
            }
        }
    }
];

const MRILesionCells: IGroupedTableCellProps[] = [
    {
        columnName: 'lesionNumber',
        content: (
            <Typography>R1</Typography>
        )
    },
    {
        columnName: 'lesionType',
        content: (
            <Input
                component={Select}
                name={`${mriLesionsKey}[0].lesionType`}
            />
        ),
        propertyPath: `${mriLesionsKey}[0].lesionType`
    },
    {
        columnName: 'lesionSide',
        content: (
            <Input
                component={Select}
                name={`${mriLesionsKey}[0].lesionSide`}
            />
        ),
        propertyPath: `${mriLesionsKey}[0].lesionSide`
    },
    {
        columnName: 'lesionLocation',
        content: (
            <Input
                component={Select}
                name={`${mriLesionsKey}[0].lesionLocation`}
            />
        ),
        propertyPath: `${mriLesionsKey}[0].lesionLocation`
    },
    {
        columnName: 'lesionSize',
        content: (
            <Input
                component={Numeric}
                name={`${mriLesionsKey}[0].lesionSize`}
            />
        ),
        propertyPath: `${mriLesionsKey}[0].lesionSize`
    },
    {
        columnName: 'lesionBirads',
        content: (
            <Input
                component={Select}
                name={`${mriLesionsKey}[0].primaryLesionBirads`}
            />
        ),
        propertyPath: `${mriLesionsKey}[0].primaryLesionBirads`
    },
    {
        columnName: 'lesionClassification',
        content: (
            <Input
                component={Select}
                name={`${mriLesionsKey}[0].primaryLesionClassification`}
            />
        ),
        propertyPath: `${mriLesionsKey}[0].primaryLesionClassification`
    }
];

const MRIReportPrimaryLesionRow: FC<IMRIReportPrimaryLesionRowProps> = () => {
    return (
        <GroupedTable
            headers={MRILesionHeader}
        >
            <GroupedTableRow 
                cells={MRILesionCells}
            />
        </GroupedTable>
    );
}

const MRIReportLesionFilter = (_item: Dtos.MRIReportLesion | null | undefined, index: number, _items: Dtos.MRIReportLesion[] | null | undefined) => {
    return index !== 0;
}

const MRIReportLesionGrid: FC<IMRIReportLesionGridProps> = () => {
    const createNewItem = useCallback((values: Dtos.MRIReportLesion[] | null | undefined) => {
        if (!values) values = [];

        let newLesion = new Dtos.MRIReportLesion ({
            lesionNumber: (values?.length ?? 0) + 1,
            enteredBy: "",
            modifiedBy: ""
        });

        return newLesion;
    }, []);
    
    return (
        <>
            <GroupedTableArrayField
                propertyPath={mriLesionsKey}
                columns={MRILesionColumns}
                createNewItem={createNewItem}
                filter={MRIReportLesionFilter}
                entityName='Secondary Lesion'
            />
        </>
    );
} 

const CEMLesionColumns: Array<IGroupedTableArrayFieldColumn<Dtos.CEMReportLesion>> = [
    {
        propertyPath: 'lesionNumber',
        TableCellProps:{
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <>
                <Typography>
                    CM<InputDisplay />
                </Typography>
            </>
        )
    },
    {
        propertyPath: 'lesionType',
        TableCellProps:{
            sx: {
                minWidth: 250,
                maxWidth: 250,
                width: 250
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionSide',
        TableCellProps:{
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionLocation',
        TableCellProps:{
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionSize',
        TableCellProps:{
            sx: {
                minWidth: 150,
                maxWidth: 150,
                width: 150
            }
        },
        content: (
            <Input
                component={Numeric}
            />
        )
    },
    {
        propertyPath: 'secondaryLesionBirads',
        TableCellProps:{
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionCorrespondance',
        TableCellProps:{
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <Input
                component={Select}
            />
        )
    },
    {
        propertyPath: 'lesionCorrespondanceLesion',
        TableCellProps:{
            sx: {
                minWidth: 200,
                maxWidth: 200,
                width: 200
            }
        },
        content: (
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={cemLesionCorrespondance}
                subscription={{ value: true }}
            >
                <Input
                    component={Select}
                />
            </CrfCondition>
        )
    }
];

const CEMLesionHeader: IGroupedTableHeaderProps[] = [
    {
        columnName: 'lesionNumber',
        content: 'Lesion Number',
        TableCellProps:{
            sx: {
                width: 150,
                minWidth: 150
            }
        }
    },
    {
        columnName: 'lesionType',
        content: 'Lesion Type',
        TableCellProps:{
            sx: {
                width: 200,
                minWidth: 200
            }
        }
    },
    {
        columnName: 'lesionSide',
        content: 'Side',
        TableCellProps:{
            sx: {
                width: '10%',
                minWidth: 200
            }
        }
    },
    {
        columnName: 'lesionLocation',
        content: "Location (o'clock)",
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 200
            }
        }
    },
    {
        columnName: 'lesionSize',
        content: 'Size (mm)',
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 200
            }
        }
    },
    {
        columnName: 'lesionBirads',
        content: 'BIRADS',
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 200
            }
        }
    },
    {
        columnName: 'lesionCorrespondance',
        content: 'Correspond to MRI Lesion?',
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 200
            }
        }
    },
    {
        columnName: 'lesionCorrespondanceLesion',
        content: 'Corresponding MRI Lesion',
        TableCellProps:{
            sx: {
                width: '20%',
                minWidth: 200
            }
        }
    }
];

const CEMLesionCells: IGroupedTableCellProps[] = [
    {
        columnName: 'lesionNumber',
        content: (
            <>
                <Typography>CM1</Typography>
                <LesionNumberUpdater 
                    lesionNumberPath={`${cemLesionsKey}[0].lesionNumber`}
                    lesionNumber={1}
                    notDonePath={cemNotDoneKey}
                />
            </>
        ),
        propertyPath: `${cemLesionsKey}[0].lesionNumber`
    },
    {
        columnName: 'lesionType',
        content: (
            <Input
                component={Select}
                name={`${cemLesionsKey}[0].lesionType`}
            />
        ),
        propertyPath: `${cemLesionsKey}[0].lesionType`
    },
    {
        columnName: 'lesionSide',
        content: (
            <Input
                component={Select}
                name={`${cemLesionsKey}[0].lesionSide`}
            />
        ),
        propertyPath: `${cemLesionsKey}[0].lesionSide`
    },
    {
        columnName: 'lesionLocation',
        content: (
            <Input
                component={Select}
                name={`${cemLesionsKey}[0].lesionLocation`}
            />
        ),
        propertyPath: `${cemLesionsKey}[0].lesionLocation`
    },
    {
        columnName: 'lesionSize',
        content: (
            <Input
                component={Numeric}
                name={`${cemLesionsKey}[0].lesionSize`}
            />
        ),
        propertyPath: `${cemLesionsKey}[0].lesionSize`
    },
    {
        columnName: 'lesionBirads',
        content: (
            <Input
                component={Select}
                name={`${cemLesionsKey}[0].primaryLesionBirads`}
            />
        ),
        propertyPath: `${cemLesionsKey}[0].primaryLesionBirads`
    },
    {
        columnName: 'lesionCorrespondance',
        content: (
            <Input
                component={Select}
                name={`${cemLesionsKey}[0].lesionCorrespondance`}
            />
        ),
        propertyPath: `${cemLesionsKey}[0].lesionCorrespondance`
    },
    {
        columnName: 'lesionCorrespondanceLesion',
        content: (
            <CrfCondition
                mode={ConditionMode.Enable}
                condition={cemLesionCorrespondance}
                subscription={{ value: true }}
            >
                <Input
                    component={Select}
                    name={`${cemLesionsKey}[0].lesionCorrespondanceLesion`}
                />
            </CrfCondition>
        ),
        propertyPath: `${cemLesionsKey}[0].lesionCorrespondanceLesion`
    }
];

const CEMReportPrimaryLesionRow: FC<ICEMReportPrimaryLesionRowProps> = () => {
    return (
        <GroupedTable
            headers={CEMLesionHeader}
        >
            <GroupedTableRow 
                cells={CEMLesionCells}
            />
        </GroupedTable>
    );
}

const CEMReportLesionFilter = (_item: Dtos.MRIReportLesion | null | undefined, index: number, _items: Dtos.MRIReportLesion[] | null | undefined) => {
    return index !== 0;
}

const CEMReportLesionGrid: FC<ICEMReportLesionGridProps> = () => {
    const createNewItem = useCallback((values: Dtos.CEMReportLesion[] | null | undefined) => {
        if (!values) values = [];

        let newLesion = new Dtos.CEMReportLesion ({
            lesionNumber: (values?.length ?? 0) + 1,
            enteredBy: "",
            modifiedBy: ""
        });

        return newLesion;
    }, []);
    
    return (
        <>
            <GroupedTableArrayField
                propertyPath={cemLesionsKey}
                columns={CEMLesionColumns}
                createNewItem={createNewItem}
                filter={CEMReportLesionFilter}
                entityName='Secondary Lesion'
            />
        </>
    );
}

const SummaryReport: FunctionComponent<ISummaryReportProps> = ({
    dialogOpen,
    setDialogOpen
}) => {
    const {
        state: {
            value: submitType
        },
        context: {
            setValue: setSubmitType
        }
    } = useScopedField<string>(`submitType`, { value: true }, false, false);

    const {
        state: {
            value: mammographyLesions
        },
    } = useScopedField<string>(mammographyLesionsKey, { value: true }, false, false);

    const {
        state: {
            value: ultrasoundLesions
        },
    } = useScopedField<string>(ultrasoundLesionsKey, { value: true }, false, false);

    const {
        state: {
            value: mriLesions
        },
    } = useScopedField<string>(mriLesionsKey, { value: true }, false, false);

    const {
        state: {
            value: mriLeft
        },
    } = useScopedField<string>(parenchymalEnhancementScoreLeftKey, { value: true }, false, false);

    const {
        state: {
            value: mriRight
        },
    } = useScopedField<string>(parenchymalEnhancementScoreRightKey, { value: true }, false, false);

    const {
        state: {
            value: cemLesions
        },
    } = useScopedField<string>(cemLesionsKey, { value: true }, false, false);

    const {
        state: {
            value: cemLeft
        },
    } = useScopedField<string>(cemParenchymalEnhancementScoreLeftKey, { value: true }, false, false);

    const {
        state: {
            value: cemNotDone
        },
    } = useScopedField<string>(cemNotDoneKey, { value: true }, false, false);

    const {
        state: {
            value: mammographyNotDone
        },
    } = useScopedField<string>(mammographyNotDoneKey, { value: true }, false, false);

    const {
        state: {
            value: ultrasoundNotDone
        },
    } = useScopedField<string>(ultrasoundNotDoneKey, { value: true }, false, false);

    const {
        state: {
            value: cemRight
        },
    } = useScopedField<string>(cemParenchymalEnhancementScoreRightKey, { value: true }, false, false);

    const onDialogClose = React.useCallback(() => {
        setDialogOpen(false);
        setSubmitType(undefined);
    }, [setDialogOpen, setSubmitType])

    const onReportComplete = React.useCallback(async (_e: MouseEvent, formActions: IFormActions) => {
        try {
            formActions?.submit();
        }
        finally {
            onDialogClose()
        }
    }, [onDialogClose]);

    const { data: lookups } = React.useContext(LookupsContext)

    return (
        <>
            <Dialog
                open={dialogOpen}
                onClose={onDialogClose}
                aria-labelledby="summary-report-title"
                fullWidth={true}
                scroll="body"
                maxWidth="md"
            >
                    <ReportTitle
                        variant="h3"
                    >
                        Summary Report
                    </ReportTitle>

                    {
                        (!mammographyNotDone || !ultrasoundNotDone) && <>
                            <ReportHeader
                                variant="h5"
                                color="secondary"
                            >
                                Mammogram/Ultrasound
                            </ReportHeader>

                            <ReportTableContainer>
                                <ReportTable>
                                    {
                                        (mammographyLesions as any as Dtos.MammographyReportLesion[])?.map(lesion => 
                                            <TableRow key={`mammography-${lesion.lesionNumber}`}>
                                                <TableCell>
                                                    <Typography>
                                                        <strong>{lesion?.lesionNumber ? 'M' + lesion.lesionNumber : ''}</strong>
                                                        {lesion?.lesionSide === Dtos.LookupLeftRightType.Left ? ', L' : lesion?.lesionSide === Dtos.LookupLeftRightType.Right ? ', R' : ', '}
                                                        {(lesion?.primaryLesionType ?? lesion?.secondaryLesionType) ? ' ' + (GetFieldLookupItem(lookups, `MammographyLesions.PrimaryLesionType`, lesion?.primaryLesionType)?.value?.toLowerCase() ?? GetFieldLookupItem(lookups, `MammographyLesions.SecondaryLesionType`, lesion?.secondaryLesionType)?.value?.toLowerCase()) : ''}
                                                        {lesion?.lesionSize ? ', ' + lesion?.lesionSize + 'mm' : ''}
                                                        {lesion?.lesionLocation ? ', ' + GetFieldLookupItem(lookups, `MammographyLesions.LesionLocation`, lesion?.lesionLocation)?.value + (GetFieldLookupItem(lookups, `MammographyLesions.LesionLocation`, lesion?.lesionLocation)?.value.includes('00') ? '' : ' oc') : ''}
                                                        {(lesion?.primaryLesionBirads != null || lesion?.secondaryLesionBirads != null) ? ', BIRADS ' + (GetFieldLookupItem(lookups, `MammographyLesions.PrimaryLesionBirads`, lesion?.primaryLesionBirads)?.value ?? GetFieldLookupItem(lookups, `MammographyLesions.SecondaryLesionBirads`, lesion?.secondaryLesionBirads)?.value) : ''}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    }
                                    {
                                        (ultrasoundLesions as any as Dtos.UltrasoundReportLesion[])?.map(lesion => 
                                            <TableRow key={`ultrasound-${lesion.lesionNumber}`}>
                                                <TableCell>
                                                    <Typography>
                                                        <strong>{lesion?.lesionNumber ? 'U' + lesion.lesionNumber : ''}</strong>
                                                        {lesion?.lesionSide === Dtos.LookupLeftRightType.Left ? ', L' : lesion?.lesionSide === Dtos.LookupLeftRightType.Right ? ', R' : ', '}
                                                        {lesion?.lesionTypeSpecify ? ' ' + lesion?.lesionTypeSpecify : (((lesion?.primaryLesionType ?? lesion?.secondaryLesionType) ? ' ' + (GetFieldLookupItem(lookups, `UltrasoundLesions.PrimaryLesionType`, lesion?.primaryLesionType)?.value?.toLowerCase() ?? GetFieldLookupItem(lookups, `UltrasoundLesions.SecondaryLesionType`, lesion?.secondaryLesionType)?.value?.toLowerCase()) : ''))}
                                                        {lesion?.lesionSize ? ', ' + lesion?.lesionSize + 'mm' : ''}
                                                        {lesion?.lesionLocation ? ', ' + GetFieldLookupItem(lookups, `UltrasoundLesions.LesionLocation`, lesion?.lesionLocation)?.value + (GetFieldLookupItem(lookups, `UltrasoundLesions.LesionLocation`, lesion?.lesionLocation)?.value.includes('00') ? '' : ' oc') : ''}
                                                    </Typography>
                                                </TableCell>
                                            </TableRow>
                                        )
                                    }
                                </ReportTable>
                            </ReportTableContainer>
                        </>
                    }

                    <ReportHeader
                        variant="h5"
                        color="secondary"
                    >
                        MRI
                    </ReportHeader>

                    <ReportTableContainer>
                        <ReportTable>
                        {
                            (mriLeft ?? mriRight) && <TableRow key={`mri-bpe`}>
                                <TableCell>
                                    <Typography>
                                        {mriLeft ? 'L - BPE ' + GetFieldLookupItem(lookups, `ParenchymalEnhancementScoreLeft`, mriLeft as any as number)?.value + ', ' : ''}
                                        {mriRight ? 'R - BPE ' + GetFieldLookupItem(lookups, `ParenchymalEnhancementScoreRight`, mriRight as any as number)?.value : ''}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        }
                        {
                            (mriLesions as any as Dtos.MRIReportLesion[])?.map(lesion => 
                                <TableRow key={`mri-${lesion.lesionNumber}`}>
                                    <TableCell>
                                        <Typography>
                                            <strong>{lesion?.lesionNumber ? 'R' + lesion.lesionNumber : ''}</strong>
                                            {lesion?.lesionSide === Dtos.LookupLeftRightType.Left ? ', L' : lesion?.lesionSide === Dtos.LookupLeftRightType.Right ? ', R' : ', '}
                                            {(lesion?.lesionType ? ' ' + GetFieldLookupItem(lookups, `MriLesions.LesionType`, lesion?.lesionType)?.value?.toLowerCase() : '')}
                                            {lesion?.lesionSize ? ', ' + lesion?.lesionSize + 'mm' : ''}
                                            {lesion?.lesionLocation ? ', ' + GetFieldLookupItem(lookups, `MriLesions.LesionLocation`, lesion?.lesionLocation)?.value + (GetFieldLookupItem(lookups, `MriLesions.LesionLocation`, lesion?.lesionLocation)?.value.includes('00') ? '' : ' oc') : ''}
                                            {(lesion?.primaryLesionBirads != null || lesion?.secondaryLesionBirads != null) ? ', BIRADS ' + (GetFieldLookupItem(lookups, `MriLesions.PrimaryLesionBirads`, lesion?.primaryLesionBirads)?.value ?? GetFieldLookupItem(lookups, `MriLesions.SecondaryLesionBirads`, lesion?.secondaryLesionBirads)?.value) : ''}
                                            {(lesion?.primaryLesionClassification ?? lesion?.secondaryLesionClassification ?? lesion.secondaryLesionClassificationMinimumDistance) ? ', ' + (GetFieldLookupItem(lookups, `MriLesions.PrimaryLesionClassification`, lesion?.primaryLesionClassification)?.value ?? GetFieldLookupItem(lookups, `MriLesions.SecondaryLesionClassification`, lesion?.secondaryLesionClassification)?.value ?? '') : ''}
                                            {lesion.secondaryLesionClassificationMinimumDistance ? ' (' + lesion.secondaryLesionClassificationMinimumDistance + 'mm)' : ''}
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            )
                        }
                        </ReportTable>
                    </ReportTableContainer>
                    
                    {
                        !cemNotDone && <>
                            <ReportHeader
                                variant="h5"
                                color="secondary"
                            >
                                CEM
                            </ReportHeader>

                            <ReportTableContainer>
                                <ReportTable>
                                {
                                    (cemLeft ?? cemRight) && <TableRow key={`mri-bpe`}>
                                        <TableCell>
                                            <Typography>
                                                {cemLeft ? 'L - BPE ' + GetFieldLookupItem(lookups, `CemParenchymalEnhancementScoreLeft`, cemLeft as any as number)?.value + ', ' : ''}
                                                {cemRight ? 'R - BPE ' + GetFieldLookupItem(lookups, `CemParenchymalEnhancementScoreRight`, cemRight as any as number)?.value : ''}
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                }
                                {
                                    (cemLesions as any as Dtos.CEMReportLesion[])?.map(lesion => 
                                        <TableRow key={`cem-${lesion.lesionNumber}`}>
                                            <TableCell>
                                                <Typography>
                                                    <strong>{lesion?.lesionNumber ? 'CM' + lesion.lesionNumber : ''}</strong>
                                                    {lesion?.lesionSide === Dtos.LookupLeftRightType.Left ? ', L' : lesion?.lesionSide === Dtos.LookupLeftRightType.Right ? ', R' : ', '}
                                                    {(lesion?.lesionType ? ' ' + GetFieldLookupItem(lookups, `CemLesions.LesionType`, lesion?.lesionType)?.value?.toLowerCase() : '')}
                                                    {lesion?.lesionSize ? ', ' + lesion?.lesionSize + 'mm' : ''}
                                                    {lesion?.lesionLocation ? ', ' + GetFieldLookupItem(lookups, `CemLesions.LesionLocation`, lesion?.lesionLocation)?.value + (GetFieldLookupItem(lookups, `CemLesions.LesionLocation`, lesion?.lesionLocation)?.value.includes('00') ? '' : ' oc') : ''}
                                                    {(lesion?.primaryLesionBirads != null || lesion?.secondaryLesionBirads != null) ? ', BIRADS ' + (GetFieldLookupItem(lookups, `CemLesions.PrimaryLesionBirads`, lesion?.primaryLesionBirads)?.value ?? GetFieldLookupItem(lookups, `CemLesions.SecondaryLesionBirads`, lesion?.secondaryLesionBirads)?.value) : ''}
                                                </Typography>
                                            </TableCell>
                                        </TableRow>
                                    )
                                }
                                </ReportTable>
                            </ReportTableContainer>
                        </>
                    }
                    
                    {
                        !!submitType && (
                            <ReadOnlyDisplay
                                mode={ReadOnlyDisplayMode.HideWhenReadOnly}
                            >
                                <StyledButtonGroup>
                                    <FormSubmitButton
                                        variant="contained"
                                        color="primary"
                                        onClick={onReportComplete}
                                    >
                                        Mark report as complete
                                    </FormSubmitButton>
                                    <FormSubmitButton
                                        variant="contained"
                                        color="primary"
                                        onClick={onDialogClose}
                                    >
                                        Cancel
                                    </FormSubmitButton>
                                </StyledButtonGroup>
                            </ReadOnlyDisplay>
                        )
                    }
                    <ReadOnlyDisplay
                        mode={ReadOnlyDisplayMode.ShowWhenReadOnly}
                        readOnly={!submitType}
                    >
                        <StyledButtonGroup>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={onDialogClose}
                            >
                                Close
                            </Button>
                        </StyledButtonGroup>
                    </ReadOnlyDisplay>
            </Dialog>
            
        </>
    );
}

const SubmitButtons: FunctionComponent<ISubmitButtonsProps> = ({
    onSubmit
}) => {
    const isMobile = useIsMobile();
    const {
        state: {
            value: reportComplete
        }
    } = useScopedField<boolean>(`complete`, {value: true}, false, false);

    const onFormSubmit = React.useCallback(async (_e: MouseEvent, formActions: IFormActions) => {
        if (!formActions?.getSubmitting()) {
            formActions?.setFieldValue('submitType', 'save');
        }

        if(onSubmit)
        {
            onSubmit(reportComplete, formActions);
        }
    }, [onSubmit, reportComplete]);

    const onFormSubmitAndReturn = React.useCallback(async (_e: MouseEvent, formActions: IFormActions) => {
        if (!formActions?.getSubmitting()) {
            formActions?.setFieldValue('submitType', 'saveAndReturn');
        }
        
        if(onSubmit)
        {
            onSubmit(reportComplete, formActions);
        }
    }, [onSubmit, reportComplete]);

    const params = useParams();
    const navigate = useNavigate();
    const { data: user} = useContext(AuthenticatedUserContext);
    const { data: patient} = useContext(PatientContext);
    const { routeParameters} = useContext(OnlinePatientManagementContext);

    const onFormCancel = useCallback(async () => {
        const institutionCode = params[routeParameters.institutionCode];

        if (user?.type === Dtos.UserType.Patient) {
            navigate(`/`);
        }
        else if (patient) {
            navigate(`/registration/${institutionCode}/${patient?.studyNumber}`)
        }
        else if (institutionCode) {
            navigate(`/registration/${institutionCode}`)
        }
        else {
            navigate(`/registration`);
        }
    }, [navigate, params, routeParameters, patient, user]);

    return (
        <>
            <ReadOnlyDisplay
                mode={ReadOnlyDisplayMode.HideWhenReadOnly}
            >
                <StyledButtonGroup>
                    {
                        !isMobile && (
                            <FormSubmitButton
                                variant="contained"
                                color="primary"
                                onClick={onFormSubmit}
                            >
                                Submit
                            </FormSubmitButton>
                        )
                    }
                    <FormSubmitButton
                        variant="contained"
                        color="primary"
                        onClick={onFormSubmitAndReturn}
                    >
                        Submit And Return
                    </FormSubmitButton>
                    <FormSubmitButton
                        variant="contained"
                        color="primary"
                        onClick={onFormCancel}
                    >
                        Cancel
                    </FormSubmitButton>
                </StyledButtonGroup>
            </ReadOnlyDisplay>
            <ReadOnlyDisplay
                mode={ReadOnlyDisplayMode.ShowWhenReadOnly}
            >
                <StyledButtonGroup>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={onFormCancel}
                    >
                        Back
                    </Button>
                </StyledButtonGroup>
            </ReadOnlyDisplay>
        </>
    );
}

const RadiologyReportForm: FunctionComponent<IRadiologyReportFormProps> = () => {

    const { data: masterGroup } = useContext(MasterGroupContext);
    const { data: collaboratingGroup } = useContext(CollaboratingGroupContext);
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);

    const {
        permissions: [
            canRadiologyReview,
            canUpdateEcrf
        ],
        loading: permissionsLoading
    } = usePermissionsByIds(
        [
            Permission.RadiologyReviewAccess,
            Permission.EcrfView
        ], 
        masterGroup?.id, 
        collaboratingGroup?.id, 
        institution?.id, 
        patient?.id
    );

    const {
        data,
        loading
    } = useGetImageReportUploadContent(patient ? 
        { patientId: patient.id } : 
        undefined
    );

    const { enqueueSnackbar } = useSnackbar();

    const isRadiologist = React.useMemo(() => {
        return !!canRadiologyReview && !canUpdateEcrf;
    }, [canRadiologyReview, canUpdateEcrf]);

    const onFormSubmitFailure: IFormSubmitFailed<Dtos.RadiologyReport, Dtos.IFormValidationError> = React.useCallback(async (_formState, _formActions, error?: unknown) => {

        let errorMessage = 'An error occurred while attempting to save the form.';

        const responseStatus = (error as any)?.responseStatus as Dtos.ResponseStatus;

        if (responseStatus?.errorCode === 'ClientViewableException') {
            errorMessage = responseStatus.message;
        }

        enqueueSnackbar(
            <>
                <AlertTitle>
                    Form Not Saved
                </AlertTitle>
                {
                    errorMessage
                }
            </>,
            { variant: 'critical' }
        );
    }, [enqueueSnackbar]);

    const [summaryReportDialogOpen, setSummaryReportDialogOpen] = React.useState<boolean>(false);

    const onSubmit = React.useCallback((reportComplete?: boolean|null, formActions?: IFormActions) => {
        if(reportComplete === true)
        {
            setSummaryReportDialogOpen(true);
        }
        else
        {
            formActions?.submit();
        }
    }, [setSummaryReportDialogOpen, summaryReportDialogOpen]);

    if (permissionsLoading) {
        return (
            <RouteLoading />
        );
    }

    return (
        <PageLayout
            breadcrumbs={<FormBreadcrumbs />}
        >
            {
                !loading && 
                <CrfForm
                    validateOn={ValidateOn.onChange}
                    readOnly={!canRadiologyReview}
                    hideButtons
                    onSubmitFailed={onFormSubmitFailure}
                    debug={AppSettings.debug}
                >
                    <ImageReportUploadView
                        data={data}
                    />
                    
                    <GroupedFields>
                        <GroupedField 
                            propertyPath="mammographyPerformed"
                            component={Checkbox}
                            LabelProps={{
                                label: (
                                    <>
                                        <Typography
                                            variant="h2"
                                        >
                                            Mammography Report (2D, Tomosynthesis)
                                        </Typography>
                                        <Typography sx={{ pt: 2 }}>
                                            Contrast Enhanced Mammogram reported after MRI section
                                        </Typography>
                                    </>
                                )
                            }}
                            formInputOptions={{
                                labelMode: InputOptionMode.UseCustom
                            }}
                            label='Not Done'
                        />
                        <CrfCondition
                            mode={ConditionMode.Enable}
                            condition={mammographyPerformed}
                            subscription={{ value: true }}
                        >
                            <GroupedField 
                                propertyPath="mammographyBreastDensityAssignment"
                                component={Select}
                            />
                            <GroupedField 
                                propertyPath="mammographyBiradsBreastDensityScore"
                                component={Select}
                            />
                            <Divider style={{ marginTop: '24px' }} />
                            <Subheading
                                variant="h3"
                            >
                                Primary Lesion
                            </Subheading>
                            <MammographyReportPrimaryLesionRow />
                            <Subheading
                                variant="h3"
                            >
                                Secondary Lesions
                            </Subheading>
                            <MammographyReportLesionGrid />
                            <ReadOnlyDisplay
                                mode={ReadOnlyDisplayMode.HideWhenReadOnly}
                            >
                                <Subheading
                                    variant="h4"
                                >
                                    *Add row/s as required for additional benign lesions
                                </Subheading>
                            </ReadOnlyDisplay>
                        </CrfCondition>
                    </GroupedFields>

                    <GroupedFields>
                        <GroupedField 
                            propertyPath="ultrasoundPerformed"
                            component={Checkbox}
                            LabelProps={{
                                label: (
                                    <Typography
                                        variant="h2"
                                    >
                                        Ultrasound Report
                                    </Typography>
                                )
                            }}
                            formInputOptions={{
                                labelMode: InputOptionMode.UseCustom
                            }}
                            label='Not Done'
                        />
                        <Divider />
                        <CrfCondition
                            mode={ConditionMode.Enable}
                            condition={ultrasoundPerformed}
                            subscription={{ value: true }}
                        >
                            <Subheading
                                variant="h3"
                            >
                                Primary Lesion
                            </Subheading>
                            <UltrasoundReportPrimaryLesionRow />
                            <Subheading
                                variant="h3"
                            >
                                Secondary Lesions
                            </Subheading>
                            <UltrasoundReportLesionGrid />
                            <ReadOnlyDisplay
                                mode={ReadOnlyDisplayMode.HideWhenReadOnly}
                            >
                                <Subheading
                                    variant="h4"
                                >
                                    *Add row/s as required for additional benign lesions
                                </Subheading>
                            </ReadOnlyDisplay>
                        </CrfCondition>
                    </GroupedFields>

                    <GroupedFields>
                        <Subheading
                            variant="h2"
                        >
                            MRI Report
                        </Subheading>
                    
                        <GroupedField 
                            propertyPath="parenchymalEnhancementScoreRight"
                            component={Select}
                        />
                        <GroupedField 
                            propertyPath="parenchymalEnhancementScoreLeft"
                            component={Select}
                        />
                        <Divider style={{ marginTop: '24px' }} />
                        <Subheading
                            variant="h3"
                        >
                            Primary Lesion
                        </Subheading>
                        <MRIReportPrimaryLesionRow />
                        <Subheading
                            variant="h3"
                        >
                            Secondary Lesions
                        </Subheading>
                        <MRIReportLesionGrid />
                        <ReadOnlyDisplay
                            mode={ReadOnlyDisplayMode.HideWhenReadOnly}
                        >
                            <Subheading
                                variant="h4"
                            >
                                *Add row/s as required for additional benign lesions
                            </Subheading>
                        </ReadOnlyDisplay>
                    </GroupedFields>

                    <GroupedFields>
                        <GroupedField 
                            propertyPath="cemPerformed"
                            component={Checkbox}
                            LabelProps={{
                                label: (
                                    <Typography
                                        variant="h2"
                                    >
                                        CEM Report
                                    </Typography>
                                )
                            }}
                            formInputOptions={{
                                labelMode: InputOptionMode.UseCustom
                            }}
                            label='Not Done'
                        />
                        <CrfCondition
                            mode={ConditionMode.Enable}
                            condition={cemPerformed}
                            subscription={{ value: true }}
                        >
                            <GroupedField 
                                propertyPath="cemParenchymalEnhancementScoreRight"
                                component={Select}
                            />
                            <GroupedField 
                                propertyPath="cemParenchymalEnhancementScoreLeft"
                                component={Select}
                            />
                            <GroupedField 
                                propertyPath="cemBreastDensityScore"
                                component={Select}
                            />
                            <GroupedField 
                                propertyPath="cemBiradsBreastDensityScore"
                                component={Select}
                            />
                            <Divider style={{ marginTop: '24px' }} />
                            <Subheading
                                variant="h3"
                            >
                                Primary Lesion
                            </Subheading>
                            <CEMReportPrimaryLesionRow />
                            <Subheading
                                variant="h3"
                            >
                                Secondary Lesions
                            </Subheading>
                            <CEMReportLesionGrid />
                            <ReadOnlyDisplay
                                mode={ReadOnlyDisplayMode.HideWhenReadOnly}
                            >
                                <Subheading
                                    variant="h4"
                                >
                                    *Add row/s as required for additional benign lesions
                                </Subheading>
                            </ReadOnlyDisplay>
                        </CrfCondition>
                    </GroupedFields>

                    <GroupedFields>
                        <RadiologistSelector 
                            disabled={isRadiologist}
                        />
                        <CrfCondition
                            mode={ConditionMode.Show}
                            condition={reportComplete}
                            subscription={{ value: true }}
                        >
                            <GroupedField 
                                propertyPath="completedBy"
                                component={Text}
                                disabled
                            />
                        </CrfCondition>
                        <CrfCondition
                            mode={ConditionMode.Show}
                            condition={reportNotComplete}
                            subscription={{ value: true }}
                        >
                            <GroupedField 
                                propertyPath="complete"
                                component={Checkbox}
                                label=""
                            />
                        </CrfCondition>

                        <ViewReportButtonContainer>
                            <Grid2 container>
                                <Grid2
                                    size={{
                                        xl: 9,
                                        lg: 9,
                                        md: 8,
                                        sm: 12,
                                        xs: 12
                                    }}
                                >
                                    
                                </Grid2>
                                <Grid2
                                    size={{
                                        xl: 3,
                                        lg: 3,
                                        md: 4,
                                        sm: 12,
                                        xs: 12
                                    }}
                                >
                                    <Button
                                        color="secondary"
                                        variant="contained"
                                        size="small"
                                        onClick={() => setSummaryReportDialogOpen(true)}
                                        sx={{mb: 2}}
                                    >
                                        View Summary Report
                                    </Button>
                                </Grid2>
                            </Grid2>
                        </ViewReportButtonContainer>
                    </GroupedFields>

                    <SourceDocuments />

                    <SummaryReport 
                        dialogOpen={summaryReportDialogOpen}
                        setDialogOpen={setSummaryReportDialogOpen}
                    />

                    <SubmitButtons onSubmit={onSubmit}/>
                </CrfForm>
            }
        </PageLayout>
    );
}

/*
 * ----------------------------------------------------------------------------------
 * Default Export
 * ----------------------------------------------------------------------------------
 */

export default RadiologyReportForm;
